import PanelCard from "@/components/PanelCard";

import DocumentsIcon from "@/res/icons/Document.svg";
import PanelsIcon from "@/res/icons/Panels.svg";
import PaymentIcon from "@/res/icons/Payment.svg";
import ProfileIcon from "@/res/icons/Settings.svg";
import { ArrowRight, DollarSign, TriangleAlert, X } from "lucide-react";
import { useTrackerEvents } from "@/queries/tracker/useTrackerEvents";
import { useNavigate } from "react-router";
import { TrackerEventPriority } from "@/api/SolarCloudApi";
import { useState } from "react";
import H1 from "@/components/ui/H1";
import { Button } from "@/components/ui/Button";

type EventTypeInfo = {
  label: string;
  icon: React.ReactNode;
  link: string;
};

const eventTypeInfo: { [key: string]: EventTypeInfo } = {
  Agreements: {
    label: "Review",
    icon: <PanelsIcon />,
    link: "/dashboard/terms",
  },
  Documents: {
    label: "Review",
    icon: <DocumentsIcon />,
    link: "/dashboard/terms",
  },
  Order: {
    label: "View",
    icon: <DocumentsIcon />,
    link: "/dashboard/orders",
  },
  OrganisationAllocations: {
    label: "Link Now",
    icon: <DollarSign />,
    link: "/dashboard/allocations/electricity",
  },
  Payment: {
    label: "View",
    icon: <PaymentIcon />,
    link: "/dashboard/payments",
  },
  Payout: {
    label: "Link Now",
    icon: <DollarSign />,
    link: "/dashboard/allocations/electricity",
  },
  Profile: {
    label: "Update",
    icon: <ProfileIcon />,
    link: "/dashboard/profile",
  },
  UserAllocations: {
    label: "Link Now",
    icon: <DollarSign />,
    link: "/dashboard/allocations/electricity",
  },
};

export default function NotificationPanel() {
  const { data: trackerEventsOptional } = useTrackerEvents();
  const navigate = useNavigate();
  const [isElectricityReminderDismissed, setIsElectricityReminderDismissed] =
    useState(false);

  const trackerEvents = (trackerEventsOptional || []).filter(event => {
    return (
      event.type === "Agreements" ||
      event.type === "Documents" ||
      event.type === "UserAllocations" ||
      event.type === "OrganisationAllocations"
    );
  });

  if (
    !trackerEvents ||
    trackerEvents.length === 0 ||
    (trackerEvents[0].priority !== TrackerEventPriority.CRITICAL &&
      trackerEvents[0].priority !== TrackerEventPriority.HIGH)
  ) {
    return null;
  }

  const event = trackerEvents[0];

  const title =
    event.title === "User Allocations" ? "Link Your Electricity" : event.title;

  const messages =
    event.title === "User Allocations"
      ? ["Link your electricity account to start saving."]
      : event.messages;

  const info = eventTypeInfo[event.type as string];

  if (event.type === "UserAllocations") {
    if (isElectricityReminderDismissed) {
      return null;
    }

    return (
      <PanelCard
        className="relative isolate overflow-hidden rounded-2xl border border-[#9A7518] bg-[radial-gradient(ellipse_at_80%_110%,rgba(246,177,27,0.22),transparent_48%),#111212] py-0 shadow-[0_0_24px_rgba(244,183,35,0.18)]"
        contentClassName="relative z-10 px-4 py-5 sm:px-6 sm:py-6 md:px-6"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-2 bg-[#FFC629]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[-2rem] left-[38%] h-20 w-1/2 rounded-[50%] bg-amber-400/15 blur-2xl"
        />
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 right-0 h-[72px] w-[62%]"
          preserveAspectRatio="none"
          viewBox="0 0 800 120"
        >
          <defs>
            <linearGradient id="electricity-alert-wave" x1="0" x2="1">
              <stop offset="0%" stopColor="#FFC629" stopOpacity="0" />
              <stop offset="55%" stopColor="#FFC629" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#FFC629" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          <path
            d="M0 88 C115 88 150 45 265 45 C392 45 424 118 558 93 C650 76 679 31 800 10"
            fill="none"
            stroke="url(#electricity-alert-wave)"
            strokeWidth="3"
          />
        </svg>
        <div className="relative flex flex-col gap-5 pr-3 sm:flex-row sm:items-center sm:gap-7 sm:pr-10">
          <div className="flex shrink-0 justify-center sm:border-r sm:border-[#8A6A1D] sm:pr-7">
            <div className="flex size-16 items-center justify-center rounded-full border-2 border-[#FFC629] text-[#FFD34D] shadow-[0_0_18px_rgba(255,198,41,0.48)] sm:size-[76px]">
              <TriangleAlert aria-hidden="true" className="size-9" strokeWidth={1.8} />
            </div>
          </div>
          <div className="min-w-0 flex-1 text-center sm:text-left">
            <p className="text-[10px] font-bold tracking-[0.18em] text-[#F4B91F]">
              ACTION REQUIRED
            </p>
            <h2 className="mt-0.5 text-2xl font-semibold leading-tight text-white sm:text-[30px]">
              Link your electricity account
            </h2>
            <p className="mt-1 text-sm text-[#B8C0CC] sm:text-base">
              Link your electricity account to receive your SolarCloud credits.
            </p>
          </div>
          {info && (
            <Button
              size="lg"
              className="shrink-0 rounded-xl border border-[#FFD34D] bg-[#FFC629] px-6 text-black shadow-[0_0_16px_rgba(255,198,41,0.2)] hover:bg-[#FFD34D] sm:min-w-[220px]"
              onClick={() => navigate(info.link)}
            >
              Link account <ArrowRight aria-hidden="true" />
            </Button>
          )}
        </div>
        <button
          aria-label="Dismiss electricity account reminder"
          className="absolute right-2 top-2 z-20 flex size-8 items-center justify-center rounded-full text-[#9CA3AF] transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC629]"
          onClick={() => setIsElectricityReminderDismissed(true)}
          type="button"
        >
          <X aria-hidden="true" className="size-5" />
        </button>
      </PanelCard>
    );
  }

  return (
    <PanelCard
      variant="teal"
      contentClassName="px-4 md:px-10"
    >
      <div className="flex flex-col sm:flex-row gap-4 overflow-hidden">
        <div className="flex flex-col flex-1 sm:max-w-2/3 gap-y-4 sm:gap-y-10">
          <H1 className="">{title}</H1>
          {messages?.map((message, index) => (
            <p key={index} className="text-md sm:text-base">
              {message}
            </p>
          ))}
        </div>
        <div
          className="mx-auto w-24 sm:w-42 h-full flex-shrink-0 [&>svg]:w-full [&>svg]:h-full"
          style={{ stroke: "black" }}
        >
          {info && info.icon}
        </div>
      </div>
      {info && (
        <div className="mt-6 flex items-center justify-center">
          <Button
            size="lg"
            variant="secondary"
            className="border border-brand-neutral-650 bg-brand-blue-100 text-black hover:bg-brand-neutral-800 hover:text-brand-blue-200"
            onClick={() => navigate(info.link)}
          >
            {info.label}
          </Button>
        </div>
      )}
    </PanelCard>
  );
}
